import {
  AuthCard,
  AuthHighlight,
  AuthHighlights,
  AuthHighlightTitle,
  AuthPanel,
  AuthShell,
  AuthTag,
  AuthVisual,
} from "taskguard-ui/auth-layout";
import { BrandMark } from "taskguard-ui/brand";
import { Button } from "taskguard-ui/button";
import { Field } from "taskguard-ui/field";
import { TextInput } from "taskguard-ui/inputs";
import { StackForm } from "taskguard-ui/surfaces";
import { CopyText, ErrorText, Eyebrow, VisuallyHiddenInput } from "taskguard-ui/text";
import { useEffect, useState } from "react";
import { useTodoStore } from "../store";

export function AuthScreen() {
  const authStatus = useTodoStore((state) => state.authStatus);
  const authError = useTodoStore((state) => state.authError);
  const registerWithPin = useTodoStore((state) => state.registerWithPin);
  const unlockWithPin = useTodoStore((state) => state.unlockWithPin);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const isSetup = authStatus === "setup";
  const isLoading = authStatus === "loading";

  useEffect(() => {
    setPin("");
    setConfirmPin("");
  }, [authStatus]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isLoading || busy) {
      return;
    }

    if (isSetup && pin !== confirmPin) {
      return;
    }

    try {
      setBusy(true);

      if (isSetup) {
        await registerWithPin(pin);
      } else {
        await unlockWithPin(pin);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <AuthCard>
        <AuthVisual>
          <BrandMark aria-hidden="true" />
          <AuthTag>TaskGuard</AuthTag>
          <h1>
            {isLoading
              ? "Opening your secure workspace."
              : isSetup
                ? "Give your workspace a private front door."
                : "Unlock your vault desk."}
          </h1>
          <CopyText>
            {isLoading
              ? "TaskGuard is checking the browser's Origin Private File System for your encrypted vault data."
              : isSetup
                ? "Set a PIN once and keep it in memory only. Vault data stays encrypted in this browser's private file system."
                : "Refreshing the page clears the in-memory PIN, so unlock again to load your encrypted vaults from this browser's private file system."}
          </CopyText>

          <AuthHighlights>
            <AuthHighlight>
              <AuthHighlightTitle>Private at rest</AuthHighlightTitle>
              <CopyText as="span">
                Vaults and passwords remain encrypted before they ever touch the browser's private file system.
              </CopyText>
            </AuthHighlight>
            <AuthHighlight>
              <AuthHighlightTitle>Shared when you choose</AuthHighlightTitle>
              <CopyText as="span">
                Only vaults you create or import are available to the sync layer.
              </CopyText>
            </AuthHighlight>
            <AuthHighlight>
              <AuthHighlightTitle>One focused list</AuthHighlightTitle>
              <CopyText as="span">
                Keep one active vault in front of you and switch workspaces from the sidebar.
              </CopyText>
            </AuthHighlight>
          </AuthHighlights>
        </AuthVisual>

        <AuthPanel>
          <Eyebrow>{isLoading ? "Storage" : "Access"}</Eyebrow>
          <h2>{isLoading ? "Checking secure storage" : isSetup ? "Create PIN" : "Unlock with PIN"}</h2>
          {isLoading ? (
            <>
              <CopyText>
                Looking for encrypted vault data in OPFS before deciding whether this browser should create a new workspace or unlock an existing one.
              </CopyText>
              {authError ? <ErrorText>{authError}</ErrorText> : null}
            </>
          ) : (
            <StackForm onSubmit={onSubmit}>
              <VisuallyHiddenInput
                aria-hidden="true"
                autoComplete="username"
                name="username"
                readOnly
                tabIndex={-1}
                value="taskguard"
              />
              <Field htmlFor="pin" label="PIN code">
                <TextInput
                  autoComplete={isSetup ? "new-password" : "current-password"}
                  id="pin"
                  inputMode="numeric"
                  name="pin"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setPin(event.currentTarget.value)
                  }
                  pattern="[0-9]*"
                  placeholder="At least 4 digits"
                  type="password"
                  value={pin}
                />
              </Field>
              {isSetup ? (
                <Field htmlFor="confirm-pin" label="Confirm PIN">
                  <TextInput
                    autoComplete="new-password"
                    id="confirm-pin"
                    inputMode="numeric"
                    name="confirmPin"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setConfirmPin(event.currentTarget.value)
                    }
                    pattern="[0-9]*"
                    placeholder="Repeat your PIN"
                    type="password"
                    value={confirmPin}
                  />
                </Field>
              ) : null}
              {isSetup && pin.length > 0 && confirmPin.length > 0 && pin !== confirmPin ? (
                <ErrorText>The PIN entries need to match.</ErrorText>
              ) : null}
              {authError ? <ErrorText>{authError}</ErrorText> : null}
              <Button block disabled={busy} type="submit">
                {busy ? "Working..." : isSetup ? "Create PIN" : "Unlock"}
              </Button>
            </StackForm>
          )}
        </AuthPanel>
      </AuthCard>
    </AuthShell>
  );
}
